import { DAY_ORDER, DayHours, DayKey, OperatingHours } from './types';

const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;

const coerceTime = (value: unknown): string | null =>
  typeof value === 'string' && TIME_RE.test(value.trim()) ? value.trim() : null;

const openDay = (open: string, close: string): DayHours => ({ closed: false, open, close });
const closedDay = (): DayHours => ({ closed: true, open: null, close: null });

// A friendly starting point when hours are first enabled: weekdays open, weekend
// closed. The user edits from here rather than an empty grid.
export const createDefaultHours = (): OperatingHours => ({
  mon: openDay('08:00', '17:00'),
  tue: openDay('08:00', '17:00'),
  wed: openDay('08:00', '17:00'),
  thu: openDay('08:00', '17:00'),
  fri: openDay('08:00', '17:00'),
  sat: closedDay(),
  sun: closedDay(),
});

// Coerce whatever the server returned into a complete, editable grid, or null
// when hours are unset. Missing days become open-with-no-times.
export const coerceOperatingHours = (input: unknown): OperatingHours | null => {
  if (!input || typeof input !== 'object') return null;
  const source = input as Record<string, any>;
  const result = {} as OperatingHours;
  for (const day of DAY_ORDER) {
    const raw = source[day] ?? {};
    const closed = Boolean(raw.closed);
    result[day] = closed
      ? closedDay()
      : { closed: false, open: coerceTime(raw.open), close: coerceTime(raw.close) };
  }
  return result;
};

export const setDayClosed = (
  hours: OperatingHours,
  day: DayKey,
  closed: boolean
): OperatingHours => ({
  ...hours,
  [day]: closed
    ? closedDay()
    : { closed: false, open: hours[day].open ?? '08:00', close: hours[day].close ?? '17:00' },
});

export const setDayTime = (
  hours: OperatingHours,
  day: DayKey,
  field: 'open' | 'close',
  value: string
): OperatingHours => ({
  ...hours,
  [day]: { ...hours[day], closed: false, [field]: value },
});

// Quick-fill: apply one open/close to every day and mark them all open.
export const applyToAllDays = (
  hours: OperatingHours,
  times: { open: string; close: string }
): OperatingHours => {
  const next = {} as OperatingHours;
  for (const day of DAY_ORDER) {
    next[day] = openDay(times.open, times.close);
  }
  return next;
};

// Copy one day's full state (times or closed) onto the chosen days.
export const copyDayTo = (
  hours: OperatingHours,
  fromDay: DayKey,
  toDays: DayKey[]
): OperatingHours => {
  const source = hours[fromDay];
  const next = { ...hours };
  for (const day of toDays) {
    if (day === fromDay) continue;
    next[day] = { ...source };
  }
  return next;
};
